import React, { useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';

import useTranslation from 'hooks/use-translation';
import { mediaQueries } from 'src/theme';
import { useModals } from 'src/components/modals';
import { ModalNames } from 'src/components/modals/modals.types';
import useAppliedRestrictedDiscounts from 'src/hooks/use-applied-restricted-discounts';
import { getShownDiscountIds, markDiscountAsShown } from 'src/utils/discount-modal-tracking';
import { InfoCircleOutline } from 'src/svg/info-circle-outline';

/**
 * POS-13717: Component to display applied restricted POS discounts.
 * This handles group/segment-restricted sale discounts that don't go through
 * the traditional "offers" system (which is for bundles/mix-and-match only).
 */
export default function RestrictedDiscountApplied() {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { restrictedDiscounts, hasRestrictedDiscount } = useAppliedRestrictedDiscounts();

  const shownModalIdsRef = useRef(getShownDiscountIds());

  const showDiscountModal = useCallback(
    (discount) =>
      openModal(ModalNames.discountQualification, {
        name: discount.menuDisplayName || discount.name,
        description: discount.menuDisplayDescription || undefined,
        image: discount.menuDisplayImage || undefined,
        specialId: discount._id,
      }),
    [openModal]
  );

  // Auto-show modal for newly applied restricted discounts
  useEffect(() => {
    if (!hasRestrictedDiscount) {
      return;
    }

    restrictedDiscounts.reduce(
      (promise, discount) =>
        promise.then(() => {
          const discountKey = String(discount.discountId || discount._id);
          const notYetShown = !shownModalIdsRef.current.has(discountKey);

          if (notYetShown) {
            markDiscountAsShown(discountKey);
            shownModalIdsRef.current.add(discountKey);
            // Return the promise from showDiscountModal to ensure sequential execution
            return showDiscountModal(discount);
          }

          return undefined;
        }),
      Promise.resolve()
    );
  }, [restrictedDiscounts, hasRestrictedDiscount, showDiscountModal]);

  // Don't render if no restricted discounts
  // Note: Feature flag check removed - we show applied restricted discounts regardless of flag
  // since the discount has already been applied by the backend
  if (!hasRestrictedDiscount) {
    return null;
  }

  return (
    <>
      {restrictedDiscounts.map((discount) => {
        const displayName = discount.menuDisplayName || discount.name;
        const discountKey = String(discount.discountId || discount._id);

        return (
          <BannerContainer
            key={discountKey}
            onClick={() => showDiscountModal(discount)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                showDiscountModal(discount);
              }
            }}
            role='button'
            tabIndex={0}
            aria-label={`View details for ${displayName}`}
            data-testid='restricted-discount-banner'
          >
            <NameContainer>
              <SpecialName>{displayName}</SpecialName>
              <InfoIconWrapper data-testid='restricted-discount-info-button'>
                <InfoCircleOutline />
              </InfoIconWrapper>
            </NameContainer>
            <AppliedBadge>
              <CheckIcon />
              <AppliedText>{t('common.discountToCartApplied', 'Applied')}</AppliedText>
            </AppliedBadge>
          </BannerContainer>
        );
      })}
    </>
  );
}

// Checkmark icon matching Figma design
const CheckIcon = () => (
  <svg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path d='M10 3L4.5 8.5L2 6' stroke='#00624a' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
);

// Main banner container - matches Figma design exactly
const BannerContainer = styled.div`
  background-color: #d1efde;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #c4e8d4;
  }

  &:focus {
    outline: 2px solid #00624a;
    outline-offset: -2px;
  }
`;

const NameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SpecialName = styled.span`
  font-weight: 700;
  font-size: 13px;
  line-height: normal;
  color: #121516;
  white-space: nowrap;

  @media ${mediaQueries.phone} {
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const InfoIconWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #121516;
  width: 16px;
  height: 16px;
`;

const AppliedBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const AppliedText = styled.span`
  font-weight: 500;
  font-size: 12px;
  line-height: normal;
  color: #00624a;
  white-space: nowrap;
`;
